import React from "react";
import {
	Button,
	Checkbox,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	LinearProgress,
	Link,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { makeStyles } from "tss-react/mui";
import { useDispatch } from "react-redux";
import { Apis } from "services/api";
import { UPLOAD_CSV_NOTE } from "helpers/constants";
import DropUpload from "components/Elements/DropUploadBox";
import AddDialogStyle from "styles/application/AddDialogStyle";
import ErrorMessageWithErrorIcon from "components/Elements/ErrorMessageWithErrorIcon";
import WarningMessageWithErrorIcon from "components/Elements/WarningMessageWithErrorIcon";
import { showError } from "../../../../../../redux/common/actions";
import useCsvImportPreview from "./hooks/useCsvImportPreview";

const AT = AddDialogStyle();

const manualPillStyle = {
	display: "inline-block",
	padding: "2px 10px",
	borderRadius: 12,
	fontSize: "12px",
	fontWeight: 600,
	background: "#d5d5d5",
	color: "#393939",
	whiteSpace: "nowrap",
};

const useStyles = makeStyles()(() => ({
	loading: {
		position: "absolute",
		width: "100%",
		left: 0,
		top: 0,
	},
	content: {
		display: "flex",
		flexDirection: "column",
		gap: 5,
		marginBottom: 15,
	},
	tableHead: {
		backgroundColor: "#D2D2D9",
		border: "1px solid",
	},
	sectionRow: {
		backgroundColor: "#f0f0f0",
	},
	sectionLabel: {
		fontWeight: 700,
		fontSize: 12,
		color: "#545353",
		letterSpacing: "0.05em",
	},
	rowError: { backgroundColor: "#fde8e8" },
	rowWarning: { backgroundColor: "#fff8e1" },
	editingRow: { backgroundColor: "#e8f4fd" },
	previewActions: {
		display: "flex",
		justifyContent: "flex-end",
		gap: 10,
		marginTop: 16,
		alignItems: "center",
	},
	selectionCount: {
		fontSize: 14,
		color: "#545353",
		marginRight: "auto",
	},
	fileError: {
		marginTop: 10,
		padding: "10px 14px",
		backgroundColor: "#fde8e8",
		border: "1px solid #f5c6cb",
		borderRadius: 4,
		color: "#721c24",
		fontSize: 14,
	},
	addRowButton: {
		marginTop: 8,
		fontSize: 13,
		color: "#307AD6",
		cursor: "pointer",
		background: "none",
		border: "none",
		padding: "4px 0",
		"&:hover": { textDecoration: "underline" },
	},
	actionCell: {
		display: "flex",
		alignItems: "center",
		gap: 2,
	},
}));

const hasErrors = (row) => Array.isArray(row.errors) && row.errors.length > 0;
const hasWarnings = (row) =>
	Array.isArray(row.warnings) && row.warnings.length > 0;

const ImportTaskPartsDialog = ({ open, handleClose, taskId, importSuccess }) => {
	const { classes } = useStyles();
	const dispatch = useDispatch();

	const {
		uploadPercentCompleted,
		setUploadPercentCompleted,
		loading,
		setLoading,
		fetchLoading,
		showProgress,
		fileError,
		cancelFileUpload,
		view,
		onDocumentUpload,
		downloadTemplate,
		closeOverride,
		resetState,
		previewRows,
		manualRows,
		convertedCsvIndices,
		selectedIndices,
		editingRow,
		draftValues,
		setDraftValues,
		selectableIndices,
		isEditing,
		allSelected,
		someSelected,
		savedManualRowsCount,
		canImport,
		handleToggleRow,
		handleToggleAll,
		startEditCsvRow,
		startEditManualRow,
		handleAddManualRow,
		handleSaveEdit,
		handleCancelEdit,
		handleDeleteManualRow,
		handleConfirmImport,
	} = useCsvImportPreview({ taskId, importSuccess, handleClose });

	// ── Inline edit cells (shared between CSV and manual rows) ────────────────

	const renderEditCells = () => (
		<>
			<TableCell>
				<TextField
					size="small"
					type="number"
					inputProps={{ min: 1 }}
					value={draftValues.qty}
					onChange={(e) =>
						setDraftValues((prev) => ({ ...prev, qty: e.target.value }))
					}
					style={{ width: 80 }}
				/>
			</TableCell>
			<TableCell>
				<TextField
					size="small"
					value={draftValues.name}
					onChange={(e) =>
						setDraftValues((prev) => ({ ...prev, name: e.target.value }))
					}
					style={{ width: "100%" }}
				/>
			</TableCell>
			<TableCell>
				<TextField
					size="small"
					value={draftValues.stockNumber}
					onChange={(e) =>
						setDraftValues((prev) => ({
							...prev,
							stockNumber: e.target.value,
						}))
					}
					style={{ width: "100%" }}
				/>
			</TableCell>
			<TableCell>
				<TextField
					size="small"
					value={draftValues.description}
					onChange={(e) =>
						setDraftValues((prev) => ({
							...prev,
							description: e.target.value,
						}))
					}
					style={{ width: "100%" }}
				/>
			</TableCell>
			<TableCell>
				<div className={classes.actionCell}>
					<IconButton
						size="small"
						onClick={handleSaveEdit}
						disabled={!draftValues.name || !draftValues.qty || Number(draftValues.qty) < 1}
					>
						<CheckIcon fontSize="small" style={{ color: "#23BB79" }} />
					</IconButton>
					<IconButton size="small" onClick={handleCancelEdit}>
						<CloseIcon fontSize="small" style={{ color: "#E21313" }} />
					</IconButton>
				</div>
			</TableCell>
		</>
	);

	// ── Render ────────────────────────────────────────────────────────────────

	return (
		<>
			{fetchLoading && <LinearProgress className={classes.loading} />}
			<Dialog open={open} onClose={closeOverride} fullWidth maxWidth="md">
				<AT.ActionContainerFileUpload>
					<DialogTitle>
						<AT.HeaderText>
							{view === "upload"
								? "Upload CSV File"
								: "Select Parts to Import"}
						</AT.HeaderText>
						{view === "upload" && (
							<AT.NoteText>{UPLOAD_CSV_NOTE}</AT.NoteText>
						)}
					</DialogTitle>
					<AT.NoteButtonContainer>
						<AT.CancelButton variant="contained" onClick={closeOverride}>
							Cancel
						</AT.CancelButton>
					</AT.NoteButtonContainer>
				</AT.ActionContainerFileUpload>

				<DialogContent>
					{view === "upload" && (
						<>
							<div className={classes.content}>
								<DropUpload
									filesUploading={loading}
									setFilesUploading={setLoading}
									isImageUploaded={false}
									uploadReturn={onDocumentUpload}
									apiPath={`${Apis.ModelTasks}/${taskId}/parts/upload`}
									showProgress={showProgress}
									uploadPercentCompleted={uploadPercentCompleted}
									setUploadPercentCompleted={setUploadPercentCompleted}
									percentMultiplyBy={100}
									cancelFileUpload={cancelFileUpload}
									isDocumentUploaded
									getError={(msg) => dispatch(showError(msg))}
								/>
								{fileError && (
									<div className={classes.fileError}>{fileError}</div>
								)}
							</div>
							<div style={{ height: "10px" }} />
							<Link
								onClick={downloadTemplate}
								style={{ cursor: "pointer", fontSize: "16px", color: "#307AD6" }}
							>
								Download Import CSV Template
							</Link>
							<div style={{ height: "10px" }} />
						</>
					)}

					{view === "preview" && (
						<>
							<Table size="small">
								<TableHead className={classes.tableHead}>
									<TableRow>
										<TableCell padding="checkbox">
											<Checkbox
												checked={allSelected}
												indeterminate={someSelected}
												onChange={handleToggleAll}
												disabled={
													selectableIndices.length === 0 || isEditing
												}
											/>
										</TableCell>
										<TableCell style={{ width: "90px" }}>Qty</TableCell>
										<TableCell style={{ width: "180px" }}>Name</TableCell>
										<TableCell style={{ width: "150px" }}>Stock Number</TableCell>
										<TableCell>Description</TableCell>
										<TableCell style={{ width: "72px" }} />
									</TableRow>
								</TableHead>
								<TableBody>
									{/* CSV rows */}
									{previewRows.map((row, i) => {
										if (convertedCsvIndices.has(i)) return null;

										const rowHasErrors = hasErrors(row);
										const rowHasWarnings = hasWarnings(row);
										const isCsvEditing =
											editingRow?.source === "csv" &&
											editingRow?.index === i;

										if (isCsvEditing) {
											return (
												<TableRow key={i} className={classes.editingRow}>
													<TableCell padding="checkbox" />
													{renderEditCells()}
												</TableRow>
											);
										}

										return (
											<TableRow
												key={i}
												hover={!rowHasErrors && !isEditing}
												onClick={
													!rowHasErrors && !isEditing
														? () => handleToggleRow(i)
														: undefined
												}
												className={
													rowHasErrors
														? classes.rowError
														: rowHasWarnings
														? classes.rowWarning
														: undefined
												}
												style={{
													cursor:
														rowHasErrors || isEditing
															? "default"
															: "pointer",
												}}
											>
												<TableCell padding="checkbox">
													<Checkbox
														checked={selectedIndices.has(i)}
														disabled={rowHasErrors || isEditing}
														onChange={() => handleToggleRow(i)}
														onClick={(e) => e.stopPropagation()}
													/>
												</TableCell>
												<TableCell>{row.qty}</TableCell>
												<TableCell>{row.name}</TableCell>
												<TableCell>{row.stockNumber}</TableCell>
												<TableCell>{row.description}</TableCell>
												<TableCell>
													<div className={classes.actionCell}>
														{rowHasErrors && (
															<ErrorMessageWithErrorIcon
																message={row.errors}
															/>
														)}
														{!rowHasErrors && rowHasWarnings && (
															<WarningMessageWithErrorIcon
																message={row.warnings}
															/>
														)}
														{!rowHasErrors && (
															<IconButton
																size="small"
																disabled={isEditing}
																onClick={(e) => {
																	e.stopPropagation();
																	startEditCsvRow(i);
																}}
															>
																<EditOutlinedIcon
																	fontSize="small"
																	style={{ color: "#307AD6" }}
																/>
															</IconButton>
														)}
													</div>
												</TableCell>
											</TableRow>
										);
									})}

									{/* Manual rows section header */}
									{manualRows.length > 0 && (
										<TableRow className={classes.sectionRow}>
											<TableCell
												colSpan={6}
												className={classes.sectionLabel}
											>
												MANUAL ENTRIES
											</TableCell>
										</TableRow>
									)}

									{/* Manual rows */}
									{manualRows.map((row, i) => {
										const isManualEditing =
											editingRow?.source === "manual" &&
											editingRow?.index === i;

										if (isManualEditing) {
											return (
												<TableRow
													key={`manual-${i}`}
													className={classes.editingRow}
												>
													<TableCell padding="checkbox" />
													{renderEditCells()}
												</TableRow>
											);
										}

										return (
											<TableRow key={`manual-${i}`}>
												<TableCell padding="checkbox">
													<span style={manualPillStyle}>Manual</span>
												</TableCell>
												<TableCell>{row.qty}</TableCell>
												<TableCell>{row.name}</TableCell>
												<TableCell>{row.stockNumber}</TableCell>
												<TableCell>{row.description}</TableCell>
												<TableCell>
													<div className={classes.actionCell}>
														<IconButton
															size="small"
															disabled={isEditing}
															onClick={() => startEditManualRow(i)}
														>
															<EditOutlinedIcon
																fontSize="small"
																style={{ color: "#307AD6" }}
															/>
														</IconButton>
														<IconButton
															size="small"
															disabled={isEditing}
															onClick={() => handleDeleteManualRow(i)}
														>
															<DeleteOutlinedIcon
																fontSize="small"
																style={{ color: "#E21313" }}
															/>
														</IconButton>
													</div>
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>

							<Button
								className={classes.addRowButton}
								disabled={isEditing}
								onClick={handleAddManualRow}
								variant="text"
							>
								+ Add Row
							</Button>

							<div className={classes.previewActions}>
								<span className={classes.selectionCount}>
									{selectedIndices.size} CSV row
									{selectedIndices.size !== 1 ? "s" : ""} selected
									{savedManualRowsCount > 0 && (
										<>
											{" + "}
											{savedManualRowsCount} manual
										</>
									)}
									{previewRows.length -
										selectableIndices.length -
										convertedCsvIndices.size >
										0 && (
										<>
											{" — "}
											{previewRows.length -
												selectableIndices.length -
												convertedCsvIndices.size}{" "}
											row
											{previewRows.length -
												selectableIndices.length -
												convertedCsvIndices.size !==
											1
												? "s"
												: ""}{" "}
											with errors
										</>
									)}
								</span>
								<AT.CancelButton
									variant="contained"
									onClick={resetState}
								>
									Back
								</AT.CancelButton>
								<AT.ConfirmButton
									variant="contained"
									disabled={!canImport}
									onClick={handleConfirmImport}
								>
									Import (
									{selectedIndices.size + savedManualRowsCount})
								</AT.ConfirmButton>
							</div>
						</>
					)}
				</DialogContent>
			</Dialog>
		</>
	);
};

export default ImportTaskPartsDialog;
